import { computed, inject, Injectable } from '@angular/core';

import { combineLatest, map, of, switchMap, take } from 'rxjs';

import { filterBoolean } from '@core/common-legacy';
import {
  HrAssistantConfigDataService,
  injectHrAssistantWalkthroughDialogOpener,
  IntelligenceSettingsDataService,
} from '@core/intelligence-logic';
import {
  TellentCurrentUserDataFacade,
  TellentCurrentUserService,
  TellentFeatureFlagsService,
} from '@core/tellent-logic';
import { AppKind, ORG_GUID, TellentOrgAbilities } from '@core/tellent-types';
import { injectTellentAppUrlBuilder } from '@core/tellent-util';
import { WindowRef } from '@core/user-interface-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';

@Injectable({ providedIn: 'root' })
export class HrAssistantService {
  private readonly intelligenceDataService = inject(IntelligenceSettingsDataService);
  private readonly hrAssistantConfigDataService = inject(HrAssistantConfigDataService);
  private readonly tellentFeatureFlagsService = inject(TellentFeatureFlagsService);
  private readonly untilDestroyed = injectUntilDestroyed();
  private readonly walkthroughOpened =
    this.tellentFeatureFlagsService.isFeatureUsed('hrAssistantUsed');
  private readonly currentUserService = inject(TellentCurrentUserDataFacade);
  private readonly canManageTellent = inject(TellentCurrentUserService).hasAbility(
    TellentOrgAbilities.ManageTellent,
  );
  private readonly openWalkthroughDialog = injectHrAssistantWalkthroughDialogOpener();
  private readonly orgGuid = inject(ORG_GUID);
  private readonly tellentSettingsLeaveUrlFactory = injectTellentAppUrlBuilder();
  private readonly windowRef = inject(WindowRef);

  public assistantEntitled = this.currentUserService.getOrgFeature('hr_assistant');
  public assistantEnabled$ = combineLatest([
    this.intelligenceDataService.getFeatureState('hrAssistantEnabled'),
    this.hrAssistantConfigDataService.configData$.pipe(
      map(data => data && data.status !== 'disabled'),
    ),
  ]).pipe(map(([state, configEnabled]) => state.enabled && configEnabled));
  public userData = computed(() => {
    const user = this.currentUserService.currentUser();
    return {
      avatar: user?.avatarThumbUrl ?? '',
      name: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`,
    };
  });
  public assistantConfig = this.hrAssistantConfigDataService.configData;
  public featureTooltipUsed = this.tellentFeatureFlagsService.isFeatureUsed(
    'hrAssistantFeatureTooltipUsed',
  );

  constructor() {
    this.loadConfig();
  }

  public openWalkthrough() {
    if (this.walkthroughOpened() || !this.assistantEntitled()) return;

    const showWalkthrough$ = this.canManageTellent()
      ? of(true)
      : this.assistantEnabled$.pipe(filterBoolean, take(1));
    showWalkthrough$
      .pipe(
        switchMap(() => this.openWalkthroughDialog(this.canManageTellent()).afterClosed),
        this.untilDestroyed,
      )
      .subscribe(result => {
        this.tellentFeatureFlagsService.markFeatureUsed('hrAssistantUsed');
        if (!result?.redirect) return;

        this.redirectToIntelligenceSettings();
      });
  }

  public markHrAssistantTooltipUsed() {
    this.tellentFeatureFlagsService.markFeatureUsed('hrAssistantFeatureTooltipUsed');
  }

  public redirectToIntelligenceSettings() {
    const url = this.tellentSettingsLeaveUrlFactory(
      AppKind.TellentAdmin,
      { id: 'intelligence' },
      this.orgGuid!,
    );
    this.windowRef.nativeWindow.location = url;
  }

  private loadConfig() {
    if (!this.assistantEntitled()) return;

    this.hrAssistantConfigDataService.fetchConfig();
    this.intelligenceDataService.fetchIfNotLoaded();
  }
}
